import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'rcm.theme';

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyResolvedTheme(theme: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  // Helps native inputs/scrollbars match the theme.
  root.style.colorScheme = theme;
}

function setIconHref(id: string, href: string) {
  const el = document.getElementById(id);
  if (el instanceof HTMLLinkElement) el.href = href;
}

/** Manual light/dark uses explicit assets. System keeps the adaptive SVG favicon. */
function applyBrandIcons(preference: ThemePreference, theme: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  const manual = preference === 'light' || preference === 'dark';
  setIconHref('rcm-favicon-ico', theme === 'dark' ? '/favicon-dark.ico' : '/favicon-light.ico');
  setIconHref(
    'rcm-favicon-svg',
    manual ? (theme === 'dark' ? '/favicon-dark.svg' : '/favicon-light.svg') : '/favicon.svg',
  );
  setIconHref(
    'rcm-apple-touch-icon',
    theme === 'dark' ? '/apple-touch-icon-dark.png' : '/apple-touch-icon-light.png',
  );
}

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(value) ? value : 'system';
  } catch {
    return 'system';
  }
}

function writeStoredPreference(preference: ThemePreference) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // ignore write failures (e.g. disabled storage)
  }
}

interface ThemeContextValue {
  themePreference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setThemePreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());

  const resolvedTheme: ResolvedTheme = themePreference === 'system' ? systemTheme : themePreference;

  useEffect(() => {
    writeStoredPreference(themePreference);
    applyResolvedTheme(resolvedTheme);
    applyBrandIcons(themePreference, resolvedTheme);
  }, [themePreference, resolvedTheme]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemTheme(media.matches ? 'dark' : 'light');

    onChange();

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }

    // Safari fallback
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  const value: ThemeContextValue = useMemo(
    () => ({
      themePreference,
      resolvedTheme,
      setThemePreference: (preference) => setThemePreferenceState(preference),
    }),
    [themePreference, resolvedTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}

